import { useParams, useNavigate } from "react-router-dom";
import type { CSSProperties } from "react";
import type { AuthUser } from "../lib/api";
import { useClientContext } from "../lib/ClientContext";
import { SettingsClients } from "../components/SettingsClients";
import { SettingsEmployees } from "../components/SettingsEmployees";
import { SettingsClientRules } from "../components/SettingsClientRules";
import { SettingsUsers } from "../components/SettingsUsers";
import { SettingsAdmin } from "../components/SettingsAdmin";
import { SettingsOcrRules } from "../components/SettingsOcrRules";
import { SettingsOcrThresholds } from "../components/SettingsOcrThresholds";
import { SettingsTestOcr } from "../components/SettingsTestOcr";
import { SettingsLiveMode } from "../components/SettingsLiveMode";
import { SettingsUpdates } from "../components/SettingsUpdates";

type Tab = "clients" | "employees" | "client-rules" | "users" | "ocr-rules" | "ocr-thresholds" | "ocr-test" | "admin" | "updates" | "live-mode";

export function SettingsPage(props: { currentUser: AuthUser }) {
  // Tab ist URL-getrieben (/settings/:tab) → deep-linkbar (z.B. /settings/updates aus dem Banner).
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();
  const ALL_TABS = ["clients", "employees", "client-rules", "ocr-rules", "ocr-thresholds", "ocr-test", "users", "admin", "updates", "live-mode"];
  const tab: Tab = urlTab && ALL_TABS.includes(urlTab) ? (urlTab as Tab) : "clients";
  const setTab = (t: Tab) => navigate(`/settings/${t}`);
  // 'developer' erbt alle admin-Rechte (Migration 033) — gleicher Check
  // wie in AppShell + requireAdmin auf der Server-Seite. Sonst zeigt die
  // Settings-Seite einem developer-User nur die User-Level-Tabs, obwohl
  // der Server alle Admin-Endpoints für ihn freigibt → UI-Drift.
  const isAdmin = props.currentUser.role === "admin" || props.currentUser.role === "developer";
  const isDeveloper = props.currentUser.role === "developer";

  // T05b (UX, Thomas 09.07.): Die Einstellungen zeigen sichtbar, was am
  // MANDANTEN hängt (Datenräume: employees, client_rules, marginalia_tokens,
  // app_settings[ocr.*] — alle client_id-gescoped) und was SYSTEMWEIT ist
  // (Mandanten-Verwaltung, Benutzer, Admin/Wartung, Updates, Live-Modus,
  // OCR-Test-Werkstatt). Der Mandant ist DIREKT HIER umschaltbar — kein
  // Umweg mehr über das Kanban.
  const { clients, selectedClientId, setSelectedClientId } = useClientContext();
  const CLIENT_TABS: Tab[] = ["employees", "client-rules", "ocr-rules", "ocr-thresholds"];
  const isClientTab = CLIENT_TABS.includes(tab);

  const groupLabel: CSSProperties = {
    fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
    color: "#6b7280", margin: "0 4px 0 12px", alignSelf: "center",
  };

  return (
    <div className="settings">
      <nav className="tabs" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <span style={{ ...groupLabel, marginLeft: 0 }}>Mandant</span>
        <select
          value={selectedClientId}
          onChange={(e) => setSelectedClientId(e.target.value)}
          title="Gilt für die Mandant-Tabs (Mitarbeiter, Regelwerk, OCR) — gleiche Auswahl wie im Kanban"
          style={{ marginRight: 6, padding: "3px 6px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}
        >
          {clients.map((cl) => (
            <option key={cl.id} value={cl.id}>{cl.name}</option>
          ))}
        </select>
        <button className={tab === "employees" ? "active" : ""} onClick={() => setTab("employees")}>Mitarbeiter</button>
        <button className={tab === "client-rules" ? "active" : ""} onClick={() => setTab("client-rules")} title="Berechnungs-Regelwerk pro Mandant (nicht über die Oberfläche editierbar)">Mandant-Regelwerk</button>
        {isAdmin && (
          <button data-perm="settings-ocr-rules" className={tab === "ocr-rules" ? "active" : ""} onClick={() => setTab("ocr-rules")}>OCR-Regeln</button>
        )}
        {isAdmin && (
          <button data-perm="settings-ocr-thresholds" className={tab === "ocr-thresholds" ? "active" : ""} onClick={() => setTab("ocr-thresholds")}>OCR-Schwellen</button>
        )}

        <span style={groupLabel}>System</span>
        <button className={tab === "clients" ? "active" : ""} onClick={() => setTab("clients")} title="Mandanten anlegen/verwalten (systemweit)">Mandanten</button>
        {isAdmin && (
          <button data-perm="settings-ocr-test" className={tab === "ocr-test" ? "active" : ""} onClick={() => setTab("ocr-test")}>OCR-Test</button>
        )}
        {isAdmin && (
          <button data-perm="settings-users" className={tab === "users" ? "active" : ""} onClick={() => setTab("users")}>Benutzer</button>
        )}
        {isAdmin && (
          <button data-perm="settings-admin" className={tab === "admin" ? "active" : ""} onClick={() => setTab("admin")}>Admin</button>
        )}
        {isAdmin && (
          <button data-perm="settings-updates" className={tab === "updates" ? "active" : ""} onClick={() => setTab("updates")}>Updates</button>
        )}
        {isDeveloper && (
          <button data-perm="settings-live-mode" className={tab === "live-mode" ? "active" : ""} onClick={() => setTab("live-mode")}>Live-Modus</button>
        )}
      </nav>
      {isClientTab && (
        <div style={{
          padding: "6px 10px", margin: "8px 0 0", fontSize: 12, borderRadius: 6,
          background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e3a5f",
        }}>
          Diese Einstellungen gelten für Mandant:{" "}
          <strong>{clients.find((cl) => cl.id === selectedClientId)?.name ?? selectedClientId}</strong>
          {" "}— umschalten oben links.
        </div>
      )}
      <section className="tab-body">
        {tab === "clients" && <SettingsClients />}
        {tab === "employees" && <SettingsEmployees onGoToClients={() => setTab("clients")} />}
        {tab === "client-rules" && <SettingsClientRules />}
        {tab === "ocr-rules" && isAdmin && <SettingsOcrRules />}
        {tab === "ocr-thresholds" && isAdmin && <SettingsOcrThresholds />}
        {tab === "ocr-test" && isAdmin && <SettingsTestOcr />}
        {tab === "users" && isAdmin && <SettingsUsers currentUser={props.currentUser} />}
        {tab === "admin" && isAdmin && <SettingsAdmin />}
        {tab === "updates" && isAdmin && <SettingsUpdates />}
        {tab === "live-mode" && isDeveloper && <SettingsLiveMode />}
      </section>
    </div>
  );
}
